import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, XCircle, Trash2, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CitizenReportsManagement = () => {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/citizen-reports`, getAuthHeaders());
      setReports(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch reports', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reportId) => {
    if (!window.confirm('Convert this report to an article?')) return;

    try {
      await axios.patch(`${API_URL}/api/admin/citizen-reports/${reportId}/approve`, {}, getAuthHeaders());
      toast({ title: 'Success', description: 'Report approved and converted to article' });
      fetchReports();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve report', variant: 'destructive' });
    }
  };

  const handleReject = async (reportId) => {
    if (!window.confirm('Reject this report?')) return;

    try {
      await axios.patch(`${API_URL}/api/admin/citizen-reports/${reportId}/reject`, {}, getAuthHeaders());
      toast({ title: 'Success', description: 'Report rejected' });
      fetchReports();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reject report', variant: 'destructive' });
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Delete this report permanently?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/citizen-reports/${reportId}`, getAuthHeaders());
      toast({ title: 'Success', description: 'Report deleted' });
      fetchReports();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete report', variant: 'destructive' });
    }
  };

  const viewDetails = (report) => {
    setSelectedReport(report);
    setShowModal(true);
  };

  const pendingReports = reports.filter(r => r.status === 'pending');
  const reviewedReports = reports.filter(r => r.status !== 'pending');

  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <CardTitle>Citizen Reporter Submissions</CardTitle>
          <CardDescription>Review and manage news reports submitted by citizens</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-600">{pendingReports.length}</div>
                  <div className="text-sm text-gray-600">Pending Review</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {reports.filter(r => r.status === 'approved').length}
                  </div>
                  <div className="text-sm text-gray-600">Approved</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600">
                    {reports.filter(r => r.status === 'rejected').length}
                  </div>
                  <div className="text-sm text-gray-600">Rejected</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="space-y-6">
              {/* Pending Reports */}
              {pendingReports.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-3">⏳ Pending Review</h3>
                  <div className="space-y-3">
                    {pendingReports.map((report) => (
                      <Card key={report.id} className="border-yellow-200">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex gap-2 items-center mb-2">
                                <Badge className="bg-yellow-600">Pending</Badge>
                                <span className="text-sm text-gray-600">
                                  {new Date(report.created_at).toLocaleString('bn-BD')}
                                </span>
                              </div>
                              <h4 className="font-bold text-gray-900">📍 {report.location}</h4>
                              <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                                {report.news_description}
                              </p>
                              <div className="mt-2 text-xs text-gray-500">
                                <span>👤 {report.name}</span>
                                <span className="mx-2">•</span>
                                <span>📞 {report.phone}</span>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewDetails(report)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApprove(report.id)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleReject(report.id)}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Reviewed Reports */}
              {reviewedReports.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-3">📋 Reviewed Reports</h3>
                  <div className="space-y-3">
                    {reviewedReports.map((report) => (
                      <Card key={report.id} className="opacity-75">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex gap-2 items-center mb-2">
                                <Badge className={report.status === 'approved' ? 'bg-green-600' : 'bg-red-600'}>
                                  {report.status === 'approved' ? 'Approved' : 'Rejected'}
                                </Badge>
                                <span className="text-sm text-gray-600">
                                  by {report.reviewed_by} on {new Date(report.reviewed_at).toLocaleString('bn-BD')}
                                </span>
                              </div>
                              <h4 className="font-bold text-gray-900">📍 {report.location}</h4>
                              <p className="text-sm text-gray-700 mt-1 line-clamp-1">
                                {report.news_description}
                              </p>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewDetails(report)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDelete(report.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {reports.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>No citizen reports submitted yet</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Details Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Citizen Report Details</DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-600">Status</label>
                <div className="mt-1">
                  <Badge className={
                    selectedReport.status === 'approved' ? 'bg-green-600' :
                    selectedReport.status === 'rejected' ? 'bg-red-600' :
                    'bg-yellow-600'
                  }>
                    {selectedReport.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Name</label>
                  <p className="text-gray-900">{selectedReport.name}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Phone</label>
                  <p className="text-gray-900">{selectedReport.phone}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600">Location</label>
                <p className="text-gray-900">{selectedReport.location}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600">News Description</label>
                <div className="mt-1 bg-gray-50 p-4 rounded border">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedReport.news_description}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <label className="font-semibold">Submitted On</label>
                  <p>{new Date(selectedReport.created_at).toLocaleString()}</p>
                </div>
                {selectedReport.reviewed_at && (
                  <div>
                    <label className="font-semibold">Reviewed On</label>
                    <p>{new Date(selectedReport.reviewed_at).toLocaleString()} by {selectedReport.reviewed_by}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CitizenReportsManagement;
